/** Store checklists and audits (backlog #20): item model and the pure scoring rules shared by API and web. */
import type { ChecklistKind, RegisterCounts, RegisterView } from './registers';
import type { ListSection } from '../formSections';

export type ChecklistItemType = 'tick' | 'yes_no_na' | 'number' | 'text' | 'photo' | 'signature';
export type ChecklistCategory = 'opening' | 'closing' | 'health_safety' | 'visual_merchandising' | 'cleaning' | 'security' | 'other';
export type ChecklistFailAction = 'none' | 'task' | 'incident';
export const CHECKLIST_CATEGORIES: { key: ChecklistCategory; label: string }[] = [
  { key: 'opening', label: 'Opening' }, { key: 'closing', label: 'Closing' }, { key: 'health_safety', label: 'Health & safety' },
  { key: 'visual_merchandising', label: 'Visual merchandising' }, { key: 'cleaning', label: 'Cleaning' }, { key: 'security', label: 'Security' },
  { key: 'other', label: 'Other' },
];
export const CHECKLIST_ITEM_TYPES: { key: ChecklistItemType; label: string }[] = [
  { key: 'tick', label: 'Tick box' }, { key: 'yes_no_na', label: 'Yes / No / N/A' }, { key: 'number', label: 'Number (with range)' },
  { key: 'text', label: 'Text' }, { key: 'photo', label: 'Photo' }, { key: 'signature', label: 'Signature' },
];

export interface ChecklistItem {
  id: string;
  type: ChecklistItemType;
  label: string;
  help?: string | null;
  required: boolean;
  /** number: acceptable range (inclusive); outside it the item fails. */
  min?: number | null;
  max?: number | null;
  unit?: string | null;
  /** Audits: points for a pass (default 1). */
  weight?: number | null;
  /** yes_no_na: which answer is a pass (default 'yes'). */
  pass_answer?: 'yes' | 'no' | null;
  on_fail?: ChecklistFailAction;
}

/** Answer values: tick boolean; yes_no_na 'yes'|'no'|'na'; number; text string; photo/signature use photo_ids / a typed name. */
export type ChecklistValue = boolean | 'yes' | 'no' | 'na' | number | string | null;

export interface ItemResult { answered: boolean; failed: boolean; points: number; max: number }

/**
 * Score one answer. N/A answers neither earn nor cost points (they leave the maximum). Unanswered
 * optional items are not failures; a required item that is unanswered counts as not done.
 */
export function evaluateItem(item: ChecklistItem, value: ChecklistValue | undefined, photoCount = 0): ItemResult {
  const weight = item.weight ?? 1;
  let answered: boolean;
  let failed = false;
  switch (item.type) {
    case 'tick': answered = value === true; break;
    case 'yes_no_na':
      answered = value === 'yes' || value === 'no' || value === 'na';
      if (value === 'na') return { answered: true, failed: false, points: 0, max: 0 };
      failed = answered && value !== (item.pass_answer ?? 'yes');
      break;
    case 'number': {
      answered = typeof value === 'number' && Number.isFinite(value);
      if (answered) failed = (item.min != null && (value as number) < item.min) || (item.max != null && (value as number) > item.max);
      break;
    }
    case 'text': answered = typeof value === 'string' && value.trim().length > 0; break;
    case 'photo': answered = photoCount > 0; break;
    case 'signature': answered = typeof value === 'string' && value.trim().length >= 2; break;
    default: answered = false;
  }
  // Text, photo and signature items are evidence: required or not, they never add to an audit's score.
  const scored = item.type === 'tick' || item.type === 'yes_no_na' || item.type === 'number';
  return { answered, failed, points: scored && answered && !failed ? weight : 0, max: scored ? weight : 0 };
}

export interface RunScore { score: number; max: number; percent: number | null; failed: number; missing_required: string[]; passed: boolean | null }

export function scoreRun(items: ChecklistItem[], answers: Record<string, { value: ChecklistValue; photo_count?: number }>, passMark: number | null, isAudit: boolean): RunScore {
  let score = 0; let max = 0; let failed = 0;
  const missing: string[] = [];
  for (const it of items) {
    const a = answers[it.id];
    const r = evaluateItem(it, a?.value, a?.photo_count ?? 0);
    if (!r.answered && it.required) missing.push(it.id);
    if (r.failed) failed++;
    score += r.points; max += r.max;
  }
  const percent = max > 0 ? Math.round((score / max) * 1000) / 10 : null;
  const passed = isAudit ? (percent == null ? true : percent >= (passMark ?? 100)) : failed === 0;
  return { score, max, percent, failed, missing_required: missing, passed };
}

export interface ChecklistTemplate {
  id: string; name: string; description: string | null; category: ChecklistCategory; is_audit: boolean; pass_mark: number | null;
  items: ChecklistItem[]; task_project_id: string | null; version: number; archived_at: string | null; updated_at: string;
  schedule_count?: number;
  /** t302u: 'register' = attendance register (no items; the people list comes from the rota). */
  kind: ChecklistKind;
  register_location_id: string | null; register_location_name?: string | null; register_team_ids: string[]; register_position_ids: string[];
  /** Registers: each Absent mark raises a task in task_project_id (opt-in). */
  absent_follow_up: boolean;
  /** t695u: the buildings it is used at; null (or empty) = all buildings. Registers use register_location_id instead. */
  location_ids?: string[] | null;
  /** t721u: the template's icon (null = the default). */
  emoji?: string | null;
  /** t725u: section headings between the items (never answered or counted). */
  sections?: ListSection[];
}
export type ChecklistAssignee = 'building' | 'on_shift' | 'position' | 'user' | 'targets';
/**
 * t704u: several people, departments / custom groups (teams), positions (on shift there that day) and buildings (people
 * based there) at once: assignee_type 'targets'. One person or one position is stored as 'user' / 'position'.
 */
export interface ChecklistTargets { people: string[]; teams: string[]; positions: string[]; buildings: string[] }
export interface ChecklistSchedule {
  id: string; template_id: string; template_name: string; location_id: string; location_name: string; weekdays: number[];
  window_start: string; window_end: string; assignee_type: ChecklistAssignee; assignee_id: string | null; assignee_name: string | null; active: boolean;
  assignee_targets?: ChecklistTargets | null;
}
/** t329u: 'scheduled' is a run that hasn't been created yet (a later day in My checklists with a date filter): read-only, id `scheduled:…`. */
export type ChecklistRunStatus = 'open' | 'in_progress' | 'completed' | 'missed' | 'scheduled';
export interface ChecklistRun {
  id: string; template_id: string; template_name: string; template_version: number; is_audit: boolean; pass_mark: number | null;
  location_id: string; location_name: string; local_date: string; due_from: string | null; due_by: string | null;
  assignee_type: ChecklistAssignee; assignee_id: string | null; assignee_name: string | null; assignee_targets?: ChecklistTargets | null;
  status: ChecklistRunStatus; started_by: string | null; completed_by: string | null; completed_by_name: string | null; completed_at: string | null;
  late: boolean; score: number | null; max_score: number | null; passed: boolean | null; failed_count: number;
  answered_count: number; item_count: number;
  /** t302u: registers count marked people / people on the list in answered_count / item_count; final counts once completed. */
  kind: ChecklistKind; register_counts: RegisterCounts | null;
}
export interface ChecklistAnswer {
  item_id: string; value: ChecklistValue; note: string | null; photo_ids: string[]; failed: boolean;
  follow_up_type: 'task' | 'incident' | null; follow_up_id: string | null; answered_by: string; answered_by_name: string; answered_at: string;
}
export interface ChecklistRunDetail extends ChecklistRun { items: ChecklistItem[]; sections?: ListSection[]; answers: ChecklistAnswer[]; signature_name: string | null; can_complete: boolean; register?: RegisterView | null }
/** Runs still being worked on; once completed (submitted and signed off) answers and photos are locked. */
export const CHECKLIST_EDITABLE_STATUSES: readonly ChecklistRunStatus[] = ['open', 'in_progress', 'missed'];
export const isChecklistRunEditable = (status: string) => (CHECKLIST_EDITABLE_STATUSES as readonly string[]).includes(status);

/** One line of a run's history (from the audit log): completion and photo removals or retakes. */
export interface ChecklistHistoryEntry {
  id: string; action: string; actor_id: string | null; actor_name: string | null; created_at: string; metadata: Record<string, unknown>;
}
/** Plain-English summary of a history line, e.g. "Tom Evans removed a photo from “Photo of the goods-in area”". */
export function describeChecklistHistory(e: Pick<ChecklistHistoryEntry, 'action' | 'actor_name' | 'metadata'>): string {
  const who = e.actor_name ?? 'Someone';
  const item = typeof e.metadata?.item === 'string' ? ` “${e.metadata.item}”` : ' an item';
  const n = Array.isArray(e.metadata?.attachment_ids) ? e.metadata.attachment_ids.length : 1;
  switch (e.action) {
    case 'ops.checklist_run.photo_removed': return `${who} removed ${n === 1 ? 'a photo' : `${n} photos`} from${item}`;
    case 'ops.checklist_run.photo_replaced': return `${who} replaced a photo on${item}`;
    case 'ops.checklist_run.completed': return `${who} completed the checklist`;
    case 'ops.checklist_run.started': return `${who} started the checklist`;
    case 'ops.checklist_run.mark_changed': {
      const name = typeof e.metadata?.person === 'string' ? e.metadata.person : 'someone';
      const lbl = (v: unknown) => (typeof v === 'string' ? ({ present: 'Present', late: 'Late', absent: 'Absent', on_leave: 'On leave' } as Record<string, string>)[v] ?? v : 'not marked');
      return `${who} changed ${name} from ${lbl(e.metadata?.from)} to ${lbl(e.metadata?.to)}`;
    }
    case 'ops.checklist_run.person_added': return `${who} added ${typeof e.metadata?.person === 'string' ? e.metadata.person : 'someone'} to the register`;
    default: return `${who}: ${e.action.replace(/^ops\.checklist_run\./, '').replace(/_/g, ' ')}`;
  }
}
/** The photo list after removing one, or swapping it for a retake in the same place. */
export const withoutPhoto = (ids: readonly string[], id: string) => ids.filter(x => x !== id);
export const swapPhoto = (ids: readonly string[], id: string, next: string) => ids.map(x => (x === id ? next : x));

export interface ChecklistReportRow { location_id: string; location_name: string; template_id: string; template_name: string; due: number; completed: number; late: number; missed: number; failed_items: number; avg_score: number | null; completion_rate: number | null;
  /** t302u: registers: people per day by their outcome that day (latest mark; Late if they were late), as the Attendance tab. */
  attendance?: Pick<RegisterCounts, 'present' | 'late' | 'absent' | 'on_leave'> | null }
export interface ChecklistTrendPoint { week: string; completed: number; failed_items: number; missed: number }

/** t695u: does this template apply at the building? (null / empty list = every building; a register only at its own.) */
export function checklistTemplateAt(t: Pick<ChecklistTemplate, 'location_ids' | 'kind' | 'register_location_id'>, locationId: string): boolean {
  if (t.kind === 'register' && t.register_location_id) return t.register_location_id === locationId;
  return !t.location_ids?.length || t.location_ids.includes(locationId);
}

/**
 * t695u: the buildings a checklist can be started (or scheduled) at by someone who may do that at `allowed`
 * ('all' = everywhere): the template's own buildings they can reach, in the given order. No cut-off.
 */
export function checklistStartBuildings<L extends { id: string }>(
  t: Pick<ChecklistTemplate, 'location_ids' | 'kind' | 'register_location_id'>, locations: readonly L[], allowed: 'all' | readonly string[],
): L[] {
  return locations.filter(l => checklistTemplateAt(t, l.id) && (allowed === 'all' || allowed.includes(l.id)));
}

/** t696u: the checklists offered under "Start a checklist": live, not registers, used at a building I can start them at. */
export function startableChecklists<T extends Pick<ChecklistTemplate, 'location_ids' | 'kind' | 'register_location_id' | 'archived_at' | 'name'>>(
  templates: readonly T[], locations: readonly { id: string }[], allowed: 'all' | readonly string[],
): T[] {
  return templates.filter(t => !t.archived_at && t.kind !== 'register' && checklistStartBuildings(t, locations, allowed).length > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
}

/** t695u: search a building list by name (case-insensitive, any word order). */
export function filterBuildings<L extends { name: string }>(locations: readonly L[], q: string): L[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  return words.length ? locations.filter(l => words.every(w => l.name.toLowerCase().includes(w))) : [...locations];
}
